import { CorporateAccount } from "../models/corporateAccountModel";
import { LocationHostUser } from "../models/locationHostUserModel";
import { CorporateAccountAdmin } from "../models/corporateAccountAdminModel";
import { CorporateAccountROI } from "../models/corporateAccountROI";
import { CorporateHostRating } from "../models/corporateHostRatingModel";
import { CorporateAccountTotalVisits } from "../models/corporateAccountTotalVisitsModel";
import { CorporatePoorConditionReportCount } from "../models/corporatePoorConditionReportCountModel";
import { CorporateDoorLockIssueCount } from "../models/corporateDoorLockIssueCountModel";
import { CorporateHostStatusRollup } from "../models/corporateHostStatusRollupModel";
import { CorporateHostRatingsList } from "../models/corporateHostRatingsListModel";
import { backendRequest } from "./backendApi";
import { backendPublicRequest } from "./backendTransport";
import exceptionService from "./exceptionService";
const call = async <T = any>(path: string, body?: any, pub = false) => {
  const r = pub ? await backendPublicRequest<T>(path, body) : await backendRequest<T>(path, body);
  if (r.error) exceptionService.logError(r.error, `CorporateAccountService:${path}`);
  return r;
};
const yes = async (path: string, body: any, pub = false) =>
  (await call<boolean>(path, body, pub)).data === true;
const rpc = async <T>(name: string, params: any, empty: T): Promise<T> => {
  const r = await call<T>("/corporate/rpc", { name, params });
  return r.data ?? empty;
};
const corporateAccountService = {
  async getCorporateAccounts(searchText: string | null): Promise<CorporateAccount[]> {
    return (await call<CorporateAccount[]>("/corporate/accounts/list", { searchText })).data ?? [];
  },
  async getCorporateAccountAdminByEmailOfUser(
    email: string
  ): Promise<CorporateAccountAdmin | null> {
    return (await call<CorporateAccountAdmin>("/corporate/admin/by-email", { email })).data ?? null;
  },
  async getCorporateAccountAdminByCorporateAccountId(
    corporateAccountId: number
  ): Promise<CorporateAccountAdmin[]> {
    return (
      (await call<CorporateAccountAdmin[]>("/corporate/admin/by-account", { corporateAccountId }))
        .data ?? []
    );
  },
  async getCorporateAccountLocationHostUsers_PerEmail(email: string): Promise<LocationHostUser[]> {
    return (
      (await call<LocationHostUser[]>("/corporate/location-host-users/by-email", { email })).data ??
      []
    );
  },
  async getCorporateAccountLocationHostUsers(
    corporateAccountId: number,
    hostId: number
  ): Promise<LocationHostUser[]> {
    return (
      (
        await call<LocationHostUser[]>("/corporate/location-host-users", {
          corporateAccountId,
          hostId,
        })
      ).data ?? []
    );
  },
  async insertLocationHostUser(locationHostUser: LocationHostUser): Promise<boolean> {
    return yes("/corporate/location-host-user/insert", { locationHostUser });
  },
  async insertAccountAdmin(admin: CorporateAccountAdmin): Promise<boolean> {
    return yes("/corporate/admin/insert", { admin });
  },
  async updateLocationHostUser(id: number, email: string): Promise<boolean> {
    return yes("/corporate/location-host-user/update", { id, email });
  },
  async updateAccountAdmin(id: number, email: string): Promise<boolean> {
    return yes("/corporate/admin/update", { id, email });
  },
  async deleteLocationHostUser(id: number): Promise<boolean> {
    return yes("/corporate/location-host-user/delete", { id });
  },
  async deleteAccountAdmin(id: number): Promise<boolean> {
    return yes("/corporate/admin/delete", { id });
  },
  async getCorporateAccount(id: number): Promise<CorporateAccount | null> {
    return (await call<CorporateAccount>("/corporate/account/by-id", { id })).data ?? null;
  },
  async getCorporateAccountUser(userId: number): Promise<CorporateAccount | null> {
    return (await call<CorporateAccount>("/corporate/account/by-owner", { userId })).data ?? null;
  },
  async insertCorporateAccount(account: CorporateAccount): Promise<number> {
    return (await call<number>("/corporate/account/insert", { account }, true)).data ?? -1;
  },
  async updateCorporateAccount(account: CorporateAccount, id: number): Promise<boolean> {
    return yes("/corporate/account/update", { account, id });
  },
  async updateAccountIsActive(id: number, isActive: boolean): Promise<boolean> {
    return yes("/corporate/account/update-active", { id, isActive });
  },
  async getCorporateROI(id: number): Promise<CorporateAccountROI[]> {
    return rpc("get_corporate_roi_lifetime", { corp_id: id }, []);
  },
  async getCorporateVisits(id: number): Promise<CorporateAccountTotalVisits[]> {
    return rpc("get_corporate_visit_counts", { corp_id: id }, []);
  },
  async getCorporateHostRatings(id: number): Promise<CorporateHostRating[]> {
    return rpc("get_corporate_ratings", { corp_id: id }, []);
  },
  async getCorporateHostPoorConditionReportsCounts(
    id: number
  ): Promise<CorporatePoorConditionReportCount[]> {
    return rpc("get_corporate_poor_condition_reports", { corp_id: id }, []);
  },
  async getCorporateHostDoorLockIssueCounts(id: number): Promise<CorporateDoorLockIssueCount[]> {
    return rpc("get_corporate_door_lock_issues", { corp_id: id }, []);
  },
  async getCorporateHostPerformance(id: number): Promise<any[]> {
    return rpc("get_corporate_host_stats", { corp_id: id }, []);
  },
  async getCorporateHostStatusRollup(id: number): Promise<CorporateHostStatusRollup | null> {
    const rows = await rpc<any[]>("get_corporate_host_stats_rollup", { corp_id: id }, []);
    return rows[0] ?? null;
  },
  async getHostRatings(id: number): Promise<CorporateHostRatingsList[] | null> {
    return rpc("get_all_ratings_for_host", { host_id: id }, []);
  },
  async saveCorporateIssueReport(
    category: string,
    description: string,
    userId: string,
    corporateAccountId: number
  ): Promise<boolean> {
    return yes("/corporate/issue-report", { category, description, userId, corporateAccountId });
  },
  async checkBusinessExistAtAddress(
    address: string,
    city: string,
    state: string,
    zip: string
  ): Promise<boolean> {
    return (
      (
        await call<boolean>(
          "/corporate/account/check-business-address",
          { address, city, state, zip },
          true
        )
      ).data === true
    );
  },
};
export default corporateAccountService;
